import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OtaPlatformIcon from 'components/@admin/@ota/OtaPlatformIcon';
import ChartTimelineVariantIcon from 'mdi-react/ChartTimelineVariantIcon';
import PackageVariantClosedIcon from 'mdi-react/PackageVariantClosedIcon';
import PinOutlineIcon from 'mdi-react/PinOutlineIcon';
import SourceBranchIcon from 'mdi-react/SourceBranchIcon';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import {
  useFollowBranchMutation,
  useListUpdatesQuery,
  usePinUpdateMutation,
  useRollbackEmbeddedMutation,
  useSetRolloutMutation,
} from 'store/app/entities/OtaSlice';
import { I18n } from 'utils/i18n';

const MODE_KEYS = {
  follow_branch: 'follow',
  fixed: 'pinned',
  rollout: 'rollout',
  rollback_to_embedded: 'rollback',
};

const getDeliveryChipLabel = line => {
  const modeKey = MODE_KEYS[line.deliveryMode] || 'follow';
  const label = I18n.t(`AdminOtaScene.RuntimeLines.modes.${modeKey}`);
  if (line.deliveryMode === 'rollout') {
    return `${label} · ${line.rolloutPercent ?? 0}%`;
  }
  return label;
};

const ActionIconButton = ({ title, color = 'default', disabled, onClick, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        color={color}
        disabled={disabled}
        onClick={onClick}
        aria-label={title}
      >
        <SvgIcon fontSize="small">{children}</SvgIcon>
      </IconButton>
    </span>
  </Tooltip>
);

ActionIconButton.propTypes = {
  title: PropTypes.string.isRequired,
  color: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

const deliveryModeChipColor = mode => {
  if (mode === 'follow_branch') return 'success';
  if (mode === 'fixed') return 'primary';
  if (mode === 'rollout') return 'info';
  if (mode === 'rollback_to_embedded') return 'warning';
  return 'default';
};

function RuntimeLineActions({
  row,
  followPending,
  pinPending,
  rolloutPending,
  rollbackPending,
  onFollow,
  onPin,
  onRollout,
  onRollback,
}) {
  const isFollowing = row.deliveryMode === 'follow_branch';
  const busy = followPending || pinPending || rolloutPending || rollbackPending;

  const pinLabel =
    row.deliveryMode === 'fixed'
      ? I18n.t('AdminOtaScene.RuntimeLines.actions.changePin')
      : I18n.t('AdminOtaScene.RuntimeLines.actions.pin');
  const rolloutLabel =
    row.deliveryMode === 'rollout'
      ? I18n.t('AdminOtaScene.RuntimeLines.actions.changeRollout')
      : I18n.t('AdminOtaScene.RuntimeLines.actions.rollout');

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
      {!isFollowing && (
        <ActionIconButton
          title={I18n.t('AdminOtaScene.RuntimeLines.actions.followBranch')}
          color="primary"
          disabled={busy}
          onClick={() => onFollow(row._id)}
        >
          <SourceBranchIcon />
        </ActionIconButton>
      )}
      {isFollowing && (
        <>
          <ActionIconButton
            title={pinLabel}
            color="primary"
            disabled={busy}
            onClick={() => onPin(row)}
          >
            <PinOutlineIcon />
          </ActionIconButton>
          <ActionIconButton
            title={rolloutLabel}
            color="primary"
            disabled={busy}
            onClick={() => onRollout(row)}
          >
            <ChartTimelineVariantIcon />
          </ActionIconButton>
          <ActionIconButton
            title={I18n.t('AdminOtaScene.RuntimeLines.actions.rollback')}
            color="warning"
            disabled={busy}
            onClick={() => onRollback(row)}
          >
            <PackageVariantClosedIcon />
          </ActionIconButton>
        </>
      )}
      {row.deliveryMode === 'fixed' && (
        <ActionIconButton
          title={pinLabel}
          color="primary"
          disabled={busy}
          onClick={() => onPin(row)}
        >
          <PinOutlineIcon />
        </ActionIconButton>
      )}
      {row.deliveryMode === 'rollout' && (
        <ActionIconButton
          title={rolloutLabel}
          color="primary"
          disabled={busy}
          onClick={() => onRollout(row)}
        >
          <ChartTimelineVariantIcon />
        </ActionIconButton>
      )}
    </Box>
  );
}

RuntimeLineActions.propTypes = {
  row: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    deliveryMode: PropTypes.string.isRequired,
  }).isRequired,
  followPending: PropTypes.bool.isRequired,
  pinPending: PropTypes.bool.isRequired,
  rolloutPending: PropTypes.bool.isRequired,
  rollbackPending: PropTypes.bool.isRequired,
  onFollow: PropTypes.func.isRequired,
  onPin: PropTypes.func.isRequired,
  onRollout: PropTypes.func.isRequired,
  onRollback: PropTypes.func.isRequired,
};

const formatRevisionLabel = (revision, updateId) => {
  if (revision != null) return `rev.${revision}`;
  if (updateId) return `…${String(updateId).slice(-10)}`;
  return null;
};

function ActiveUpdateCell({ line }) {
  if (line.deliveryMode === 'follow_branch') {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          lineHeight: 1.5,
        }}
      >
        {I18n.t('AdminOtaScene.RuntimeLines.activeUpdateFollowHint')}
      </Typography>
    );
  }
  if (line.deliveryMode === 'fixed' && line.fixedUpdateId) {
    const id = String(line.fixedUpdateId);
    const label = formatRevisionLabel(line.fixedUpdateRevision, id);
    return (
      <Typography
        variant="body2"
        sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.secondary' }}
        title={id}
      >
        {label}
      </Typography>
    );
  }
  if (line.deliveryMode === 'rollout' && line.rolloutUpdateId) {
    const id = String(line.rolloutUpdateId);
    const label = formatRevisionLabel(line.rolloutUpdateRevision, id);
    return (
      <Typography
        variant="body2"
        sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.secondary' }}
        title={id}
      >
        {label}
      </Typography>
    );
  }
  if (line.deliveryMode === 'rollback_to_embedded') {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {I18n.t('AdminOtaScene.RuntimeLines.activeUpdateEmbeddedHint')}
      </Typography>
    );
  }
  return (
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
      }}
    >
      —
    </Typography>
  );
}

ActiveUpdateCell.propTypes = {
  line: PropTypes.shape({
    deliveryMode: PropTypes.string.isRequired,
    fixedUpdateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fixedUpdateRevision: PropTypes.number,
    rolloutUpdateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rolloutUpdateRevision: PropTypes.number,
  }).isRequired,
};

const formatUpdateOptionLabel = update => {
  const rev =
    update.revision != null ? `rev.${update.revision}` : `…${String(update._id).slice(-8)}`;
  return `${rev} · ${update.branch}`;
};

function UpdatePicker({ channelName, line, value, onChange }) {
  const { data, isFetching } = useListUpdatesQuery(
    {
      channelName,
      limit: 100,
      platform: line?.platform,
      runtimeVersion: line?.runtimeVersion,
      enabled: 'true',
    },
    { skip: !channelName || !line?.platform || !line?.runtimeVersion },
  );

  const updates = useMemo(() => data?.updates || [], [data?.updates]);

  return (
    <FormControl fullWidth size="small" disabled={isFetching}>
      <InputLabel id="ota-runtime-line-update-label">
        {I18n.t('AdminOtaScene.RuntimeLines.updateId')}
      </InputLabel>
      <Select
        labelId="ota-runtime-line-update-label"
        label={I18n.t('AdminOtaScene.RuntimeLines.updateId')}
        value={value}
        onChange={e => onChange(e.target.value)}
        displayEmpty
        renderValue={selected => {
          if (!selected) return '';
          const match = updates.find(u => String(u._id) === selected);
          return match ? formatUpdateOptionLabel(match) : `…${selected.slice(-10)}`;
        }}
      >
        {updates.map(update => (
          <MenuItem key={update._id} value={String(update._id)}>
            <Box>
              <Typography variant="body2">{formatUpdateOptionLabel(update)}</Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontFamily: 'monospace',
                }}
              >
                …{String(update._id).slice(-12)}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {isFetching && (
        <FormHelperText sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={14} />
          {I18n.t('AdminOtaScene.RuntimeLines.loadingUpdates')}
        </FormHelperText>
      )}
      {!isFetching && updates.length === 0 && (
        <FormHelperText>{I18n.t('AdminOtaScene.RuntimeLines.noUpdatesForLine')}</FormHelperText>
      )}
    </FormControl>
  );
}

UpdatePicker.propTypes = {
  channelName: PropTypes.string.isRequired,
  line: PropTypes.shape({
    platform: PropTypes.string.isRequired,
    runtimeVersion: PropTypes.string.isRequired,
  }),
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const RuntimeLinesTable = ({ channelName, lines }) => {
  const [follow, { isLoading: followPending }] = useFollowBranchMutation();
  const [rollback, { isLoading: rollbackPending }] = useRollbackEmbeddedMutation();
  const [setRollout, { isLoading: rolloutPending }] = useSetRolloutMutation();
  const [pin, { isLoading: pinPending }] = usePinUpdateMutation();

  const [rolloutDialog, setRolloutDialog] = useState(null);
  const [pinDialog, setPinDialog] = useState(null);
  const [rollbackDialog, setRollbackDialog] = useState(null);
  const [updateId, setUpdateId] = useState('');
  const [percent, setPercent] = useState('100');

  const onOpenRollout = line => {
    setUpdateId(line.rolloutUpdateId ? String(line.rolloutUpdateId) : '');
    setPercent(line.rolloutPercent ? String(line.rolloutPercent) : '100');
    setRolloutDialog(line);
  };

  const onOpenPin = line => {
    setUpdateId(line.fixedUpdateId ? String(line.fixedUpdateId) : '');
    setPinDialog(line);
  };

  const onRolloutSubmit = async () => {
    if (!rolloutDialog || !updateId) return;
    await setRollout({
      id: rolloutDialog._id,
      updateId,
      percent: parseInt(percent, 10) || 0,
    }).unwrap();
    setRolloutDialog(null);
    setUpdateId('');
    setPercent('100');
  };

  const onPinSubmit = async () => {
    if (!pinDialog || !updateId) return;
    await pin({ id: pinDialog._id, updateId }).unwrap();
    setPinDialog(null);
    setUpdateId('');
  };

  const onRollbackConfirm = async () => {
    if (!rollbackDialog) return;
    await rollback(rollbackDialog._id).unwrap();
    setRollbackDialog(null);
  };

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
          mb: 1.5,
        }}
      >
        {I18n.t('AdminOtaScene.RuntimeLines.actionsHint')}
      </Typography>
      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 88, whiteSpace: 'nowrap' }}>
                {I18n.t('AdminOtaScene.RuntimeLines.colPlatform')}
              </TableCell>
              <TableCell sx={{ width: 112, whiteSpace: 'nowrap' }}>
                {I18n.t('AdminOtaScene.RuntimeLines.colRuntime')}
              </TableCell>
              <TableCell sx={{ width: 160, whiteSpace: 'nowrap' }}>
                {I18n.t('AdminOtaScene.RuntimeLines.colDelivery')}
              </TableCell>
              <TableCell>{I18n.t('AdminOtaScene.RuntimeLines.colActiveUpdate')}</TableCell>
              <TableCell align="right" sx={{ width: 120, whiteSpace: 'nowrap' }}>
                {I18n.t('AdminOtaScene.RuntimeLines.colActions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map(line => (
              <TableRow key={line._id} hover>
                <TableCell>
                  <OtaPlatformIcon platform={line.platform} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {line.runtimeVersion}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="soft"
                    color={deliveryModeChipColor(line.deliveryMode)}
                    label={getDeliveryChipLabel(line)}
                    sx={{ alignSelf: 'flex-start' }}
                  />
                </TableCell>
                <TableCell sx={{ py: 1.25 }}>
                  <ActiveUpdateCell line={line} />
                </TableCell>
                <TableCell align="right" sx={{ py: 1.25 }}>
                  <RuntimeLineActions
                    row={line}
                    followPending={followPending}
                    pinPending={pinPending}
                    rolloutPending={rolloutPending}
                    rollbackPending={rollbackPending}
                    onFollow={id => follow(id)}
                    onPin={onOpenPin}
                    onRollout={onOpenRollout}
                    onRollback={setRollbackDialog}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!pinDialog} onClose={() => setPinDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{I18n.t('AdminOtaScene.RuntimeLines.pinDialog.title')}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2,
            }}
          >
            {I18n.t('AdminOtaScene.RuntimeLines.pinDialog.body')}
          </Typography>
          <UpdatePicker
            channelName={channelName}
            line={pinDialog}
            value={updateId}
            onChange={setUpdateId}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPinDialog(null)}>{I18n.t('AdminOtaScene.cancel')}</Button>
          <Button variant="contained" onClick={onPinSubmit} disabled={!updateId}>
            {I18n.t('AdminOtaScene.RuntimeLines.actions.pin')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!rolloutDialog} onClose={() => setRolloutDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{I18n.t('AdminOtaScene.RuntimeLines.rolloutDialog.title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {I18n.t('AdminOtaScene.RuntimeLines.rolloutDialog.body')}
          </Typography>
          <UpdatePicker
            channelName={channelName}
            line={rolloutDialog}
            value={updateId}
            onChange={setUpdateId}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label={I18n.t('AdminOtaScene.RuntimeLines.percent')}
            value={percent}
            onChange={e => setPercent(e.target.value)}
            slotProps={{ htmlInput: { min: 0, max: 100 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRolloutDialog(null)}>{I18n.t('AdminOtaScene.cancel')}</Button>
          <Button variant="contained" onClick={onRolloutSubmit} disabled={!updateId}>
            {I18n.t('AdminOtaScene.RuntimeLines.actions.rollout')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!rollbackDialog}
        onClose={() => setRollbackDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{I18n.t('AdminOtaScene.RuntimeLines.rollbackDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {I18n.t('AdminOtaScene.RuntimeLines.rollbackDialog.body')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackDialog(null)}>{I18n.t('AdminOtaScene.cancel')}</Button>
          <Button variant="contained" color="warning" onClick={onRollbackConfirm}>
            {I18n.t('AdminOtaScene.RuntimeLines.actions.rollback')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

RuntimeLinesTable.propTypes = {
  channelName: PropTypes.string.isRequired,
  lines: PropTypes.arrayOf(PropTypes.shape({ _id: PropTypes.string.isRequired })).isRequired,
};

export default RuntimeLinesTable;
